if (!customElements.get('flipzy-collaboration-videos')) {
  customElements.define('flipzy-collaboration-videos', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup || !('IntersectionObserver' in window)) return;
      const videos = [...this.querySelectorAll('video[data-flipzy-autoplay]')];
      if (!videos.length) return;

      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const listeners = [];
      const states = new Map();
      this.userPaused ||= new WeakSet();
      let connected = true;
      const listen = (target, type, handler) => {
        target.addEventListener(type, handler);
        listeners.push(() => target.removeEventListener(type, handler));
      };
      const pause = (video, state) => {
        state.request += 1;
        state.pending = false;
        state.automatic = false;
        if (!video.paused) {
          state.expectedPauses += 1;
          video.pause();
        }
      };
      const update = (video, state) => {
        if (!connected) return;
        if (!state.visible || document.hidden || (motion.matches && state.automatic)) {
          pause(video, state);
          return;
        }
        if (motion.matches || this.userPaused.has(video) || state.blocked || state.pending ||
            !video.paused || !video.muted) return;

        state.pending = true;
        state.automatic = true;
        const request = ++state.request;
        const failed = () => {
          if (!connected || request !== state.request) return;
          state.pending = false;
          state.automatic = false;
          state.blocked = true;
        };
        try {
          Promise.resolve(video.play()).then(() => {
            if (!connected || request !== state.request) return;
            state.pending = false;
            if (!state.visible || document.hidden || motion.matches) pause(video, state);
          }, failed);
        } catch (_) {
          failed();
        }
      };

      videos.forEach(video => {
        const state = {
          visible: false,
          blocked: false,
          pending: false,
          automatic: false,
          expectedPauses: 0,
          request: 0,
        };
        states.set(video, state);
        video.muted = video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.controls = true;
        listen(video, 'pause', () => {
          if (state.expectedPauses) {
            state.expectedPauses -= 1;
            return;
          }
          this.userPaused.add(video);
          state.request += 1;
          state.pending = false;
          state.automatic = false;
        });
        listen(video, 'play', () => {
          if (!state.automatic) {
            this.userPaused.delete(video);
            state.blocked = false;
          }
          if (!state.visible || document.hidden || (motion.matches && state.automatic)) {
            pause(video, state);
          }
        });
        listen(video, 'error', () => {
          state.blocked = true;
          pause(video, state);
        });
      });

      const observer = new IntersectionObserver(entries => {
        if (!connected) return;
        entries.forEach(entry => {
          const state = states.get(entry.target);
          if (!state) return;
          state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
          update(entry.target, state);
        });
      }, { threshold: [0, 0.25] });
      videos.forEach(video => observer.observe(video));
      const updateAll = () => states.forEach((state, video) => update(video, state));
      listen(document, 'visibilitychange', updateAll);
      listen(motion, 'change', updateAll);

      this.cleanup = () => {
        connected = false;
        observer.disconnect();
        listeners.forEach(remove => remove());
        states.forEach((state, video) => pause(video, state));
      };
    }

    disconnectedCallback() {
      this.cleanup?.();
      this.cleanup = null;
    }
  });
}
